'use client'

import { featureFlagKeys } from 'feature-flags/constants/featureFlags'
import { useFeatureFlag } from 'feature-flags/hooks/useFeatureFlag/useFeatureFlag'
import { Sheet } from 'fukku/Sheet'
import { IconDownSmall } from 'icons/components/IconDownSmall'
import { useLabels } from 'labels/hooks/useLabels/useLabels'
import { cx } from 'utils/cx'

import { dataTestIds } from '../../../constants/dataTestIds/dataTestIds'
import { useAnalytics } from '../../../contexts/AnalyticsProvider/AnalyticsProvider'
import { useSizes } from '../../../contexts/SizesProvider/SizesProvider'
import type { CommonProps } from '../../../types/CommonProps'
import type { PdpFeatureFlags } from '../../../types/FeatureFlags'
import { SizeGuide } from '../../SizeGuide'
import { ITEM_SIZES, SizeItem } from '../SizeItem/SizeItem'
import { SizesList } from '../SizesList/SizesList'

import styles from './SizesSheet.module.scss'
import text from 'fukku/styles/texts.module.scss'

type SizesSheetProps = Pick<CommonProps, 'color'> & {
	className?: string
}

export function SizesSheet({
	color,
	className,
}: SizesSheetProps): React.ReactNode {
	const {
		variables: {
			config: { sizeGuide },
		},
	} = useFeatureFlag<PdpFeatureFlags>(featureFlagKeys.GenesisPdp)

	const { t } = useLabels()
	const { sendShowSizeSelectorEvent } = useAnalytics()
	const { selectedSize, isUniqueSize, isSheetOpen, openSheet } = useSizes()
	const withSheet = !isUniqueSize

	function onSelectSizeLabelClick(): void {
		openSheet(true)
		sendShowSizeSelectorEvent()
	}

	function onClose(): void {
		openSheet(false)
	}

	return (
		<div
			className={className}
			data-testid={dataTestIds['pdp.productInfo.sizeSelector']}
		>
			{selectedSize ? (
				<SizeItem
					size={selectedSize}
					className={styles.sizesSheetButton}
					withArrow={!isUniqueSize}
					extraInfoLayout='row'
					itemHeight={ITEM_SIZES.SHEET}
					isUnique={isUniqueSize}
					withSheet
				/>
			) : (
				<button
					className={cx(styles.sizesSheetButton, styles.selectable)}
					onClick={onSelectSizeLabelClick}
				>
					<span className={text.bodySRegular}>
						{t('pdp.modal.sizeSelector.title')}
					</span>
					<IconDownSmall />
				</button>
			)}
			{withSheet && (
				<Sheet
					name='pdp.sheetSizeSelector'
					header={
						<p className={cx(styles.sizesSheetHeader, text.titleM)}>
							{t('pdp.modal.sizeSelector.title')}
						</p>
					}
					body={
						<SizesList
							sizes={color.sizes}
							selectedSize={selectedSize}
							withSheet
						/>
					}
					open={isSheetOpen}
					onClose={onClose}
					bubble={sizeGuide && <SizeGuide isSheet />}
				/>
			)}
		</div>
	)
}
